import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowDown, Loader2, AlertTriangle, Minus, ArrowDownRight } from 'lucide-react';
import { useCandidateSubscriptionActions } from '@/hooks/useCandidateSubscriptionActions';
import { useTranslation } from 'react-i18next';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { useQuery } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { SERVICE_KEYS, SERVICE_KEY_LABELS } from '@/lib/serviceKeys';
import { DowngradePricingSummary } from '@/components/billing/DowngradePricingSummary';
import type { PricingDisplayContract } from '@/lib/billing/pricingDisplay';

interface Plan {
  id: string;
  plan_key: string;
  plan_name: string;
  price_monthly_sar: number;
  standard_price_sar: number | null;
  price_yearly_sar?: number | null;
  price_annual_sar?: number | null;
  platform?: string;
  is_free?: boolean | null;
}

interface CandidateDowngradeSectionProps {
  subscription: {
    id?: string;
    billing_cycle?: string | null;
    downgrade_plan_id?: string | null;
    renewal_date?: string | null;
  } | null | undefined;
  currentPlan: Plan | null | undefined;
  allPlans: Plan[];
  candidateId?: string;
}

/** Service limit entry from plan_services */
interface ServiceLimit {
  service_key: string;
  usage_limit: number | null;
  limit_type: string; // 'unlimited' | 'monthly' | 'total'
}

/** Computed diff entry for display */
interface LimitChange {
  service_key: string;
  label: string;
  currentLimit: string;
  newLimit: string;
  isReduction: boolean;
}

const SERVICE_LABELS: Record<string, string> = {
  [SERVICE_KEYS.CV_BUILDER]: SERVICE_KEY_LABELS[SERVICE_KEYS.CV_BUILDER],
  [SERVICE_KEYS.CV_REVIEW]: SERVICE_KEY_LABELS[SERVICE_KEYS.CV_REVIEW],
  [SERVICE_KEYS.INTERVIEW_PRACTICE]: SERVICE_KEY_LABELS[SERVICE_KEYS.INTERVIEW_PRACTICE],
  [SERVICE_KEYS.JOB_DISCOVERY]: SERVICE_KEY_LABELS[SERVICE_KEYS.JOB_DISCOVERY],
};

const isForkSeekPlanPlatform = (platform?: string) => platform === 'forkseek' || platform === 'seek';

function formatLimit(limit: number | null, limitType: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (limitType === 'unlimited' || limit === null) return t('seek.subscription.downgrade.unlimited');
  if (limitType === 'total') return t('seek.subscription.downgrade.totalLimit', { count: limit });
  return t('seek.subscription.downgrade.monthlyLimit', { count: limit });
}

interface PlanServiceRow {
  plan_id: string;
  usage_limit: number | null;
  limit_type: string | null;
  services?: { service_key?: string | null } | null;
}

/**
 * Candidate-facing UI for scheduling and cancelling plan downgrades.
 * Shows real plan_services diff when a target plan is selected.
 */
export function CandidateDowngradeSection({
  subscription,
  currentPlan,
  allPlans,
  candidateId,
}: CandidateDowngradeSectionProps) {
  const { t, i18n } = useTranslation();
  const { scheduleDowngrade, cancelDowngrade } = useCandidateSubscriptionActions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');

  const billingCycle = subscription?.billing_cycle === 'yearly' ? 'yearly' : 'monthly';
  const comparablePrice = (plan: Plan | null | undefined) => {
    if (!plan) return 0;
    if (billingCycle === 'yearly') {
      return plan.price_yearly_sar ?? plan.price_annual_sar ?? 0;
    }
    return plan.standard_price_sar ?? plan.price_monthly_sar ?? 0;
  };
  const currentPrice = comparablePrice(currentPlan);

  const downgradePlans = allPlans.filter(
    (p) =>
      !p.is_free &&
      comparablePrice(p) < currentPrice &&
      isForkSeekPlanPlatform(p.platform)
  );

  const hasScheduledDowngrade = !!subscription?.downgrade_plan_id;
  const downgradePlanName = hasScheduledDowngrade
    ? allPlans.find((p) => p.id === subscription.downgrade_plan_id)?.plan_name
    : null;

  const renewalDate = subscription?.renewal_date
    ? formatLocalizedDate(subscription.renewal_date, 'MMM dd, yyyy', i18n.resolvedLanguage ?? i18n.language)
    : null;
  const quoteTargetPlanId = selectedPlanId || subscription?.downgrade_plan_id || '';
  const quoteTargetPlan = allPlans.find((candidate) => candidate.id === quoteTargetPlanId);
  const { data: downgradeQuote, isLoading: downgradeQuoteLoading } = useQuery<{
    classification?: string;
    next_renewal_date?: string | null;
    renewal_date?: string | null;
    renewal_pricing_display?: PricingDisplayContract;
  }>({
    queryKey: ['candidate-downgrade-pricing-quote', candidateId, quoteTargetPlanId],
    queryFn: async () => {
      const { data, error } = await seekSupabase.functions.invoke('quote-upgrade-price', {
        body: {
          owner_type: 'candidate',
          owner_id: candidateId,
          target_plan_id: quoteTargetPlanId,
        },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!candidateId && !!quoteTargetPlanId,
  });
  const hasAuthoritativeDowngradePricing = downgradeQuote?.classification === 'downgrade'
    && !!downgradeQuote.renewal_pricing_display;

  // ── Fetch plan_services for current and selected plan ──
  const relevantPlanIds = useMemo(() => {
    const ids = [currentPlan?.id].filter(Boolean);
    if (selectedPlanId) ids.push(selectedPlanId);
    if (subscription?.downgrade_plan_id) ids.push(subscription.downgrade_plan_id);
    return [...new Set(ids)];
  }, [currentPlan?.id, selectedPlanId, subscription?.downgrade_plan_id]);

  const { data: planServicesData } = useQuery({
    queryKey: ['plan-services-diff', relevantPlanIds],
    queryFn: async () => {
      if (relevantPlanIds.length === 0) return [];
      const { data, error } = await seekSupabase
        .from('plan_services')
        .select('plan_id, usage_limit, limit_type, services!inner(service_key)')
        .in('plan_id', relevantPlanIds);
      if (error) throw error;
      return data as PlanServiceRow[];
    },
    enabled: relevantPlanIds.length > 0,
  });

  // ── Compute diff for selected plan ──
  const limitChanges = useMemo((): LimitChange[] => {
    const targetPlanId = selectedPlanId || subscription?.downgrade_plan_id;
    if (!planServicesData || !currentPlan?.id || !targetPlanId) return [];

    const currentLimits: Record<string, ServiceLimit> = {};
    const targetLimits: Record<string, ServiceLimit> = {};

    for (const row of planServicesData) {
      const svcKey = row.services?.service_key;
      if (!svcKey) continue;
      const entry: ServiceLimit = {
        service_key: svcKey,
        usage_limit: row.usage_limit,
        limit_type: row.limit_type || 'unlimited',
      };
      if (row.plan_id === currentPlan.id) currentLimits[svcKey] = entry;
      if (row.plan_id === targetPlanId) targetLimits[svcKey] = entry;
    }

    const changes: LimitChange[] = [];

    // Services lost entirely
    for (const key of Object.keys(currentLimits)) {
      if (!targetLimits[key]) {
        changes.push({
          service_key: key,
          label: SERVICE_LABELS[key] || key,
          currentLimit: formatLimit(currentLimits[key].usage_limit, currentLimits[key].limit_type, t),
          newLimit: t('seek.subscription.downgrade.removed'),
          isReduction: true,
        });
      }
    }

    // Services with reduced limits
    for (const key of Object.keys(currentLimits)) {
      if (!targetLimits[key]) continue;
      const curr = currentLimits[key];
      const tgt = targetLimits[key];

      // Check if limit is actually reduced
      const currIsUnlimited = curr.limit_type === 'unlimited' || curr.usage_limit === null;
      const tgtIsUnlimited = tgt.limit_type === 'unlimited' || tgt.usage_limit === null;

      if (currIsUnlimited && !tgtIsUnlimited) {
        // Unlimited → capped
        changes.push({
          service_key: key,
          label: SERVICE_LABELS[key] || key,
          currentLimit: t('seek.subscription.downgrade.unlimited'),
          newLimit: formatLimit(tgt.usage_limit, tgt.limit_type, t),
          isReduction: true,
        });
      } else if (!currIsUnlimited && !tgtIsUnlimited && (tgt.usage_limit ?? 0) < (curr.usage_limit ?? 0)) {
        // Both capped, target lower
        changes.push({
          service_key: key,
          label: SERVICE_LABELS[key] || key,
          currentLimit: formatLimit(curr.usage_limit, curr.limit_type, t),
          newLimit: formatLimit(tgt.usage_limit, tgt.limit_type, t),
          isReduction: true,
        });
      } else if (curr.limit_type === 'monthly' && tgt.limit_type === 'total') {
        // Monthly → total (more restrictive)
        changes.push({
          service_key: key,
          label: SERVICE_LABELS[key] || key,
          currentLimit: formatLimit(curr.usage_limit, curr.limit_type, t),
          newLimit: formatLimit(tgt.usage_limit, tgt.limit_type, t),
          isReduction: true,
        });
      }
    }

    return changes;
  }, [planServicesData, currentPlan?.id, selectedPlanId, subscription?.downgrade_plan_id, t]);

  const confirmDowngrade = () => {
    if (!selectedPlanId || !subscription?.id || !hasAuthoritativeDowngradePricing) return;
    scheduleDowngrade.mutate(
      { subscriptionId: subscription.id, downgradePlanId: selectedPlanId },
      { onSuccess: () => { setDialogOpen(false); setSelectedPlanId(''); } },
    );
  };

  const handleCancelDowngrade = () => {
    if (!subscription?.id) return;
    cancelDowngrade.mutate(subscription.id);
  };

  if (downgradePlans.length === 0 && !hasScheduledDowngrade) return null;

  // ── Compute diff for the scheduled downgrade banner ──
  const scheduledChanges = hasScheduledDowngrade ? limitChanges : [];

  return (
    <>
      {/* ── Scheduled Downgrade Banner ── */}
      {hasScheduledDowngrade && (
        <Card className="rounded-xl border-blue-500/30 bg-blue-50/50 dark:bg-blue-900/10">
          <CardContent className="p-4 flex items-start gap-3">
            <ArrowDown className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-blue-800 dark:text-blue-300">{t('seek.subscription.downgrade.scheduledTitle')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('seek.subscription.downgrade.scheduledPrefix')} <strong>{downgradePlanName ?? t('seek.subscription.downgrade.lowerPlan')}</strong> {t('seek.subscription.downgrade.scheduledSuffix')}
                {renewalDate && <> (<strong>{renewalDate}</strong>)</>}.
                {' '}{t('seek.subscription.downgrade.currentRemainsActive')}
              </p>
              <DowngradePricingSummary
                targetPlan={downgradePlanName ?? quoteTargetPlan?.plan_name ?? t('seek.subscription.downgrade.lowerPlan')}
                effectiveDate={downgradeQuote?.next_renewal_date ?? downgradeQuote?.renewal_date ?? subscription?.renewal_date}
                renewalPricing={downgradeQuote?.renewal_pricing_display}
              />
              {scheduledChanges.length > 0 && (
                <div className="mt-2 space-y-1">
                  {scheduledChanges.map((change) => (
                    <div key={change.service_key} className="text-xs flex items-center gap-1.5 text-blue-700 dark:text-blue-400">
                      <ArrowDownRight className="w-3 h-3 shrink-0" />
                      <span>{change.label}: {change.currentLimit} → {change.newLimit}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="mt-3 rounded-xl border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/30"
                onClick={handleCancelDowngrade}
                disabled={cancelDowngrade.isPending}
              >
                {cancelDowngrade.isPending ? (
                  <><Loader2 className="w-3.5 h-3.5 me-1.5 animate-spin" />{t('seek.subscription.downgrade.cancelling')}</>
                ) : (
                  t('seek.subscription.downgrade.cancelDowngrade')
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Downgrade Button ── */}
      {downgradePlans.length > 0 && !hasScheduledDowngrade && (
        <Button
          variant="ghost"
          className="rounded-xl text-muted-foreground"
          onClick={() => setDialogOpen(true)}
        >
          <ArrowDown className="w-4 h-4 me-1.5" />
          {t('seek.subscription.downgrade.downgradePlan')}
        </Button>
      )}

      {/* ── Downgrade Scheduling Dialog ── */}
      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ArrowDown className="w-5 h-5 text-muted-foreground" />
              {t('seek.subscription.downgrade.scheduleTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-2">
                <p className="text-sm">
                  {t('seek.subscription.downgrade.currentPlanPrefix')} <strong>{currentPlan?.plan_name}</strong> {t('seek.subscription.downgrade.currentPlanSuffix')}
                  {renewalDate && <> (<strong>{renewalDate}</strong>)</>}.
                  {' '}{t('seek.subscription.downgrade.onlyTakesEffectThen')}
                </p>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    {t('seek.subscription.downgrade.downgradeTo')}
                  </label>
                  <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t('seek.subscription.downgrade.selectPlan')} />
                    </SelectTrigger>
                    <SelectContent>
                      {downgradePlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          <span>{plan.plan_name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedPlanId && (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-3 space-y-2">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />{t('seek.subscription.downgrade.impactPreview')}
                    </p>

                    {/* ── Real plan diff ── */}
                    {limitChanges.length > 0 ? (
                      <div className="space-y-1.5">
                        {limitChanges.map((change) => (
                          <div key={change.service_key} className="flex items-center gap-2 text-sm">
                            <Minus className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                            <span className="text-muted-foreground">
                              <strong>{change.label}</strong>: {change.currentLimit}
                              <span className="mx-1">→</span>
                              <span className="text-amber-700 dark:text-amber-400 font-medium">{change.newLimit}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t('seek.subscription.downgrade.allServicesRemain')}
                      </p>
                    )}

                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside mt-2">
                      <li>{t('seek.subscription.downgrade.currentPlanUntil', { date: renewalDate ?? t('seek.subscription.downgrade.renewal') })}</li>
                      <li>{t('seek.subscription.downgrade.canCancelBeforeRenewal')}</li>
                    </ul>
                    {downgradeQuoteLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('common.loading')}
                      </div>
                    ) : (
                      <DowngradePricingSummary
                        targetPlan={quoteTargetPlan?.plan_name ?? t('common.notAvailable')}
                        effectiveDate={downgradeQuote?.next_renewal_date ?? downgradeQuote?.renewal_date ?? subscription?.renewal_date}
                        renewalPricing={downgradeQuote?.renewal_pricing_display}
                      />
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('seek.subscription.downgrade.keepCurrentPlan')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDowngrade}
              disabled={!selectedPlanId || scheduleDowngrade.isPending || downgradeQuoteLoading || !hasAuthoritativeDowngradePricing}
              className="bg-muted text-foreground hover:bg-muted/80 border border-border"
            >
              {scheduleDowngrade.isPending ? (
                <><Loader2 className="w-4 h-4 me-1.5 animate-spin" />{t('seek.subscription.downgrade.scheduling')}</>
              ) : (
                t('seek.subscription.downgrade.scheduleDowngrade')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
